import { useMemo } from 'react';
import { Button, Tooltip } from '@ds';
import {
  OrSettingsType,
  RouteTypeSettingsType,
  SuggestedSettingsEnum,
  SuggestedSettingsType,
} from '@lp/features/RoutePlanner/types';
import { useRouteResources } from '@lp/features/RoutePlanner/hooks/useRouteResources';
import { useSuggestedSettings } from './useSuggestedSettings.hook';
import { RouteTypeDropdown } from './RouteTypeDropdown';
import { OrganTransportationDropdown } from './OrganTransportationDropdown';
import { OrTimeSettings } from './OrTimeSettings';
import { HubsDropdown } from './HubsDropdown';
import { CSDropdown } from './CSDropdown';
import { RSDropdown } from './RSDropdown';
import { StartTimeSettings } from './StartTimeSettings';
import { StartLocationSettings } from './StartLocationSettings';
import { validateSelectedSettings } from './utils/validation';

type SuggestedRoutesSettingsProps = {
  currentSettings?: SuggestedSettingsType[];
  canReset?: boolean;
  applySettings: (data: SuggestedSettingsType[]) => Promise<void>;
};

export const SuggestedRoutesSettings = ({
  currentSettings,
  applySettings,
  canReset = false,
}: SuggestedRoutesSettingsProps) => {
  const { ClinicalSpecialist, OCSConsole } = useRouteResources();
  const {
    caseOrTime,
    settingsList,
    disabledRouteTypes,
    handleSettingsChange,
    handleRouteTypeChange,
    handleStartLocationChange,
    handleDeploymentHubChange,
    handleSettingsReset,
  } = useSuggestedSettings(currentSettings);

  const { routeTypeSettings, orTimeSettings, hasSelectedCS, hasDeploymentHub } = useMemo(() => {
    const byType = new Map<SuggestedSettingsType['type'], SuggestedSettingsType>();

    for (const setting of settingsList) {
      if (!byType.has(setting.type)) byType.set(setting.type, setting);
    }

    return {
      routeTypeSettings: byType.get(SuggestedSettingsEnum.RouteType) as RouteTypeSettingsType,
      orTimeSettings: (byType.get(SuggestedSettingsEnum.OR) as OrSettingsType) ?? null,
      hasSelectedCS: !!byType.get(SuggestedSettingsEnum.CS)?.value,
      hasDeploymentHub: !!byType.get(SuggestedSettingsEnum.DeploymentHub)?.value,
    };
  }, [settingsList]);

  const searchValidation = useMemo(() => {
    return validateSelectedSettings(settingsList, ClinicalSpecialist, OCSConsole, disabledRouteTypes);
  }, [settingsList, ClinicalSpecialist, OCSConsole, disabledRouteTypes]);

  return (
    <div className="w-full flex gap-1 flex-col p-3 rounded-lg bg-base-80">
      <div className="flex gap-2 flex-wrap items-center">
        {settingsList.map((item) => {
          switch (item.type) {
            case SuggestedSettingsEnum.RouteType:
              return (
                <RouteTypeDropdown
                  key={item.type}
                  data={item}
                  disabledRouteTypes={disabledRouteTypes}
                  handleRouteTypeChange={handleRouteTypeChange}
                />
              );
            case SuggestedSettingsEnum.OR:
              return (
                <OrTimeSettings
                  key={item.type}
                  data={item}
                  caseOrTime={caseOrTime}
                  handleSettingsChange={handleSettingsChange}
                />
              );
            case SuggestedSettingsEnum.CS:
              return orTimeSettings ? (
                <CSDropdown
                  key={item.type}
                  data={item}
                  orTimeSettings={orTimeSettings}
                  isDisabled={hasDeploymentHub}
                  handleSettingsChange={handleSettingsChange}
                />
              ) : null;
            case SuggestedSettingsEnum.RS:
              return orTimeSettings ? (
                <RSDropdown
                  key={item.type}
                  data={item}
                  orTimeSettings={orTimeSettings}
                  handleSettingsChange={handleSettingsChange}
                />
              ) : null;
            case SuggestedSettingsEnum.OrganTransportationType:
              return (
                <OrganTransportationDropdown key={item.type} data={item} handleSettingsChange={handleSettingsChange} />
              );
            case SuggestedSettingsEnum.DeploymentHub:
              return (
                <HubsDropdown
                  key={item.type}
                  type={SuggestedSettingsEnum.DeploymentHub}
                  isDisabled={hasSelectedCS}
                  data={item}
                  handleSettingsChange={handleDeploymentHubChange}
                />
              );
            case SuggestedSettingsEnum.StartLocation:
              return (
                <StartLocationSettings
                  routeType={routeTypeSettings.value}
                  key={item.type}
                  data={item}
                  handleSettingsChange={handleStartLocationChange}
                />
              );
            case SuggestedSettingsEnum.StartTime:
              return <StartTimeSettings key={item.type} data={item} handleSettingsChange={handleSettingsChange} />;
            case SuggestedSettingsEnum.ArrivalHub:
              return (
                <HubsDropdown
                  key={item.type}
                  type={SuggestedSettingsEnum.ArrivalHub}
                  data={item}
                  handleSettingsChange={handleSettingsChange}
                />
              );
            default:
              return null;
          }
        })}
      </div>
      <div className="w-full flex justify-end gap-2 items-center mt-6">
        {canReset && (
          <Button size="sm" variant="outline" onPress={handleSettingsReset}>
            Reset to Defaults
          </Button>
        )}
        <Tooltip
          isDisabled={searchValidation.isValid}
          content={
            <div className="flex flex-col gap-1">
              {searchValidation.errors.map((e) => (
                <span key={e}>{e}</span>
              ))}
            </div>
          }
        >
          <div>
            <Button isDisabled={!searchValidation.isValid} size="sm" onPress={() => applySettings(settingsList)}>
              Search
            </Button>
          </div>
        </Tooltip>
      </div>
    </div>
  );
};
